import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, ShieldX, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const ROLES = ["", "advertiser", "mall_owner", "super_admin"];
const STATUSES = ["", "active", "pending", "suspended"];

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (role) params.role = role;
      if (status) params.status = status;
      const { data } = await api.get("/super-admin/users", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setRows([]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, status]);

  async function approve(uid) {
    try {
      await api.patch(`/super-admin/users/${uid}/approve`, { status: "active", mall_ids: [] });
      toast.success("Approved");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }
  async function setStatusOf(uid, s) {
    try {
      await api.patch(`/super-admin/users/${uid}/status`, { status: s });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }
  async function del(uid) {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/super-admin/users/${uid}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  const pendingCount = rows.filter((u) => u.status === "pending").length;

  return (
    <div data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">Console · Users</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">Platform users</h1>
          <p className="mt-1 text-neutral-400 text-sm">
            {pendingCount > 0 ? <span className="text-amber-500">{pendingCount} pending approval · </span> : null}
            {rows.length} total
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={role} onChange={(e) => setRole(e.target.value)} className="max-w-[180px]" data-testid="users-role">
            {ROLES.map((r) => <option key={r} value={r}>{r ? r.replace("_", " ") : "All roles"}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="max-w-[180px]" data-testid="users-status">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="users-create-btn">
            <Plus size={14} /> New user
          </button>
        </div>
      </div>

      {loading ? <div className="mt-10 text-neutral-500">Loading…</div> : rows.length === 0 ? (
        <div className="mt-10 surface p-12 text-center text-neutral-500" data-testid="users-empty">No users match.</div>
      ) : (
        <div className="mt-8 surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-left text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <tr>
                <th className="p-4">User</th>
                <th className="p-4">Role</th>
                <th className="p-4">Status</th>
                <th className="p-4">Malls</th>
                <th className="p-4">Created</th>
                <th className="p-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-white/5" data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-neutral-500">{u.email}</div>
                    {u.company && <div className="text-[11px] text-neutral-500">{u.company}</div>}
                  </td>
                  <td className="p-4 text-xs uppercase tracking-[0.15em] text-amber-500">{u.role?.replace("_", " ")}</td>
                  <td className="p-4">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="p-4 text-xs text-neutral-400">
                    {u.role === "mall_owner" ? (u.mall_ids?.length || 0) : "—"}
                  </td>
                  <td className="p-4 text-xs text-neutral-500 whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 justify-end">
                      {u.status === "pending" && u.role === "mall_owner" && (
                        <button onClick={() => approve(u.id)} className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1" data-testid={`approve-${u.id}`}>
                          <ShieldCheck size={12} /> Approve
                        </button>
                      )}
                      {u.status === "active" && u.role !== "super_admin" && (
                        <button onClick={() => setStatusOf(u.id, "suspended")} className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1">
                          <ShieldX size={12} /> Suspend
                        </button>
                      )}
                      {u.status === "suspended" && (
                        <button onClick={() => setStatusOf(u.id, "active")} className="text-xs text-emerald-400 hover:underline">Reactivate</button>
                      )}
                      {u.role !== "super_admin" && (
                        <button onClick={() => del(u.id)} className="text-neutral-500 hover:text-red-400" data-testid={`user-del-${u.id}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateUserModal onClose={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    suspended: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${map[status] || "bg-white/5 text-neutral-400 border-white/10"}`}>{status}</span>;
}

function CreateUserModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "mall_owner", company: "", phone: "", status: "active",
  });
  const [malls, setMalls] = useState([]);
  const [pickedMalls, setPickedMalls] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/super-admin/malls", { params: { limit: 500 } }).then((r) => setMalls(r.data)).catch(() => {});
  }, []);

  async function submit() {
    if (!form.name || !form.email || form.password.length < 8) {
      return toast.error("Name, email, and password (min 8 chars) are required");
    }
    setSaving(true);
    try {
      const payload = { ...form, mall_ids: form.role === "mall_owner" ? pickedMalls : [] };
      await api.post("/super-admin/users", payload);
      toast.success("User created");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  function toggleMall(id) {
    setPickedMalls((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="create-user-modal">
      <div className="surface max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">New user</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cu-role">
              <option value="mall_owner">Mall admin</option>
              <option value="advertiser">Advertiser</option>
              <option value="super_admin">Super admin</option>
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option>active</option><option>pending</option><option>suspended</option>
            </select>
          </Field>
          <Field label="Name"><input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email"><input type="email" data-testid="cu-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Password (min 8)"><input type="text" data-testid="cu-pwd" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Company" full><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          {form.role === "mall_owner" && (
            <Field label={`Assign malls (${pickedMalls.length} selected)`} full>
              <div className="border border-white/10 max-h-48 overflow-y-auto p-2 space-y-1 text-sm bg-black/30">
                {malls.length === 0 && <div className="text-neutral-500 text-xs p-2">No malls yet. Create malls first.</div>}
                {malls.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 cursor-pointer">
                    <input type="checkbox" checked={pickedMalls.includes(m.id)} onChange={() => toggleMall(m.id)} />
                    <span className="flex-1">{m.name} <span className="text-xs text-neutral-500">· {m.city}</span></span>
                  </label>
                ))}
              </div>
            </Field>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="cu-submit">{saving ? "Creating…" : "Create user"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
